import { useState } from 'react';
import { ChevronRight, RotateCcw } from 'lucide-react';
import { SEKCE } from '../../lib/moduly';

export const NIC = 'nic';
export const VIDI = 'vidi';
export const UPRAVUJE = 'upravuje';

const UROVNE = [
    { v: NIC, label: '—', title: 'Bez přístupu' },
    { v: VIDI, label: 'Vidí', title: 'Může otevřít a číst' },
    { v: UPRAVUJE, label: 'Upravuje', title: 'Může i měnit' },
];

/** Šablona + výjimky → výsledné přístupy (stejně jako Opravneni::slozit v PHP). */
export function slozitPristupy(sablona = {}, vyjimky = {}) {
    const vysledek = { ...(sablona || {}) };
    for (const [klic, uroven] of Object.entries(vyjimky || {})) {
        if (uroven === NIC) delete vysledek[klic];
        else vysledek[klic] = uroven;
    }
    return vysledek;
}

const ikonaModulu = (kod) => SEKCE.flatMap((s) => s.items).find((i) => i.id === kod)?.Icon;

function UrovenPrepinac({ hodnota, onZmena, barva, disabled, isDark }) {
    return (
        <div role="radiogroup" style={{
            display: 'inline-flex', flexShrink: 0, padding: 2, gap: 2, borderRadius: 6,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            opacity: disabled ? 0.5 : 1,
        }}>
            {UROVNE.map((u) => {
                const aktivni = hodnota === u.v;
                const pozadi = !aktivni ? 'transparent'
                    : u.v === UPRAVUJE ? barva
                        : u.v === VIDI ? `${barva}33`
                            : (isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)');
                const text = !aktivni ? 'var(--text-muted)'
                    : u.v === UPRAVUJE ? '#fff' : u.v === VIDI ? barva : 'var(--text-primary)';
                return (
                    <button
                        key={u.v}
                        type="button"
                        role="radio"
                        aria-checked={aktivni}
                        title={u.title}
                        disabled={disabled}
                        onClick={(e) => { e.stopPropagation(); onZmena(u.v); }}
                        style={{
                            minWidth: u.v === NIC ? 28 : 62, height: 24, padding: '0 8px',
                            borderRadius: 4, border: 'none', cursor: disabled ? 'default' : 'pointer',
                            background: pozadi, color: text,
                            fontSize: 11.5, fontWeight: aktivni ? 600 : 500,
                            transition: 'background 0.12s ease, color 0.12s ease',
                        }}
                    >
                        {u.label}
                    </button>
                );
            })}
        </div>
    );
}

function Upraveno({ onVratit, disabled }) {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <span style={{
                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
                background: 'rgba(255,138,0,0.14)', color: '#FF8A00',
            }}>upraveno</span>
            {onVratit && !disabled && (
                <button type="button" title="Vrátit na šablonu" onClick={(e) => { e.stopPropagation(); onVratit(); }}
                    style={{ display: 'inline-flex', padding: 3, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    <RotateCcw size={12} />
                </button>
            )}
        </span>
    );
}

/**
 * Přístupy po modulech a podstránkách — editor pro šablonu i pro uživatele.
 *
 * Každý řádek má přepínač úrovně: — (bez přístupu) / Vidí / Upravuje.
 * Modul i sekce mají přepínač pro všechny své podstránky naráz; když se
 * podstránky liší, žádný segment nesvítí.
 *
 * Režim uživatele (`sablona` zadaná): hodnoty = šablona + výjimky. Co se od
 * šablony liší, je označené „upraveno" a jde jedním klikem vrátit.
 *
 * Klíč přístupu: `modul.podstranka`, u modulu bez podstránek kód modulu
 * (viz App\Services\Opravneni).
 *
 * @param {Object} props
 * @param {Array} props.katalog - GET /api/profily/moduly
 * @param {Object} props.hodnoty - { klic: 'vidi'|'upravuje' }
 * @param {Object|null} [props.sablona] - Šablona uživatele (režim uživatele)
 * @param {(klice: string[], uroven: string) => void} props.onZmena
 * @param {(klice: string[]) => void} [props.onVratit] - Vrátit na šablonu
 * @param {boolean} [props.disabled]
 * @param {boolean} props.isDark
 */
export default function PristupyStrom({ katalog, hodnoty, sablona = null, onZmena, onVratit, disabled = false, isDark }) {
    const [rozbalene, setRozbalene] = useState(() => new Set());
    const rezimUzivatele = sablona !== null;
    const hranice = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-default)';

    const uroven = (klic) => hodnoty[klic] ?? NIC;
    const upraveno = (klic) => rezimUzivatele && uroven(klic) !== (sablona[klic] ?? NIC);
    // Společná úroveň skupiny klíčů, nebo null, když se liší
    const spolecna = (klice) => {
        const u = new Set(klice.map(uroven));
        return u.size === 1 ? [...u][0] : null;
    };
    const prepni = (kod) => setRozbalene((s) => {
        const n = new Set(s);
        if (n.has(kod)) n.delete(kod); else n.add(kod);
        return n;
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {katalog.map((sekce) => {
                const klice = sekce.items.flatMap((m) => m.klice);
                const sPristupem = sekce.items.filter((m) => m.klice.some((k) => uroven(k) !== NIC)).length;
                return (
                    <section key={sekce.sekce} style={{ border: `1px solid ${hranice}`, borderRadius: 8, overflow: 'hidden' }}>
                        <header style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 8px 12px',
                            background: `${sekce.color}12`, borderBottom: `1px solid ${hranice}`,
                        }}>
                            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: sekce.color }}>
                                {sekce.sekce}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>
                                {sPristupem}/{sekce.items.length} modulů
                            </span>
                            <UrovenPrepinac hodnota={spolecna(klice)} barva={sekce.color} disabled={disabled} isDark={isDark}
                                onZmena={(u) => onZmena(klice, u)} />
                        </header>

                        {sekce.items.map((modul, i) => {
                            const Icon = ikonaModulu(modul.kod);
                            const maPodstranky = modul.podstranky.length > 0;
                            const otevreny = rozbalene.has(modul.kod);
                            const modulUpraven = modul.klice.some(upraveno);
                            const pocetSPristupem = modul.klice.filter((k) => uroven(k) !== NIC).length;
                            return (
                                <div key={modul.kod} style={{ borderTop: i ? `1px solid ${hranice}` : 'none' }}>
                                    <div
                                        onClick={maPodstranky ? () => prepni(modul.kod) : undefined}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px 7px 8px',
                                            cursor: maPodstranky ? 'pointer' : 'default',
                                        }}
                                    >
                                        <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', color: 'var(--text-muted)', flexShrink: 0 }}>
                                            {maPodstranky && (
                                                <ChevronRight size={14} style={{ transform: otevreny ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                                            )}
                                        </span>
                                        {Icon && (
                                            <span style={{
                                                width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                background: `${sekce.color}1a`, color: sekce.color,
                                            }}>
                                                <Icon size={14} strokeWidth={2} />
                                            </span>
                                        )}
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{modul.nazev}</span>
                                            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {maPodstranky
                                                    ? `${pocetSPristupem}/${modul.klice.length} podstránek${modul.popis ? ` · ${modul.popis}` : ''}`
                                                    : modul.popis}
                                            </span>
                                        </span>
                                        {modulUpraven && (
                                            <Upraveno disabled={disabled} onVratit={onVratit ? () => onVratit(modul.klice) : null} />
                                        )}
                                        <UrovenPrepinac hodnota={spolecna(modul.klice)} barva={sekce.color} disabled={disabled} isDark={isDark}
                                            onZmena={(u) => onZmena(modul.klice, u)} />
                                    </div>

                                    {maPodstranky && otevreny && (
                                        <div style={{ padding: '0 10px 6px 60px', display: 'flex', flexDirection: 'column' }}>
                                            {modul.podstranky.map((p) => (
                                                <div key={p.klic} style={{
                                                    display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0',
                                                    borderTop: `1px dashed ${hranice}`,
                                                }}>
                                                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>{p.nazev}</span>
                                                    {upraveno(p.klic) && (
                                                        <Upraveno disabled={disabled} onVratit={onVratit ? () => onVratit([p.klic]) : null} />
                                                    )}
                                                    <UrovenPrepinac hodnota={uroven(p.klic)} barva={sekce.color} disabled={disabled} isDark={isDark}
                                                        onZmena={(u) => onZmena([p.klic], u)} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                );
            })}
        </div>
    );
}
